/**
 * voice-v8 — Login-View (Password + Magic-Link-Fallback).
 *
 * Primärer Pfad: Email + Password. Fallback-Link für Magic-Link blendet
 * ein zweites Formular ein — der Server schickt einen Mail-Link, der nach
 * Klick die Session-Cookie setzt.
 *
 * Wir ersetzen mit dieser View den Inhalt vom Sprechen-Tab solange der
 * User nicht eingeloggt ist (und Auth aktiviert ist).
 */

import { holeAnmeldeSchutz, loginWithPassword, requestMagicLink } from "./auth-api.js";

export class LoginView {
  /**
   * @param hinweis Ein Satz über dem Anmelden, warum gerade angemeldet
   *   werden muss (Rückkehr aus dem Panel, Ticket 2713 V2).
   */
  constructor(
    root: HTMLElement,
    onSuccess: () => void = () => window.location.reload(),
    hinweis?: string,
  ) {
    while (root.firstChild) root.removeChild(root.firstChild);
    root.classList.add("login-view");
    // Pre.109: Login-Route immer im Dark-Mode rendern (egal was OS-Theme
    // sagt). Light-Mode-Default war zu kontrastschwach (Sub-Agent-Audit).
    document.body.dataset.mode = "dark";

    const wrap = document.createElement("div");
    wrap.className = "login-card";

    const logo = document.createElement("img");
    logo.className = "login-logo";
    logo.src = "/icon.svg";
    logo.alt = "Vespera";
    logo.width = 96;
    logo.height = 96;
    wrap.appendChild(logo);

    const h1 = document.createElement("h1");
    h1.className = "login-title";
    h1.textContent = "Vespera";
    wrap.appendChild(h1);

    const intro = document.createElement("p");
    intro.className = "login-intro";
    intro.textContent = "Melde dich an, um zu sprechen.";
    wrap.appendChild(intro);

    if (hinweis) {
      const h = document.createElement("p");
      h.className = "login-hinweis";
      h.setAttribute("role", "status");
      h.textContent = hinweis;
      wrap.appendChild(h);
    }

    /*
     * 28.09.2026 (Ticket 2713 V2): der Anmelde-Schutz dieses Browsers — gleich
     * beim Zeigen geholt. Das Passwort schickt ihn mit; der Google-Start der
     * App braucht ihn SOFORT beim Tippen (ein window.open nach einem await
     * verwirft die App-WebView still), darum liegt er vorher bereit.
     */
    let schutz = holeAnmeldeSchutz();
    let schutzJetzt: string | null = null;
    void schutz.then((w) => {
      schutzJetzt = w;
    });

    // Vespera: Sign in with Google (Identitaets-Login).
    {
      const params = new URLSearchParams(window.location.search);
      const loginErr = params.get("login_error");
      if (loginErr) {
        const errLine = document.createElement("p");
        errLine.className = "login-error-banner";
        errLine.textContent =
          loginErr === "not_allowed"
            ? "Dieses Konto ist noch nicht freigeschaltet — deine Anfrage wurde an den Admin geschickt."
            : "Google-Anmeldung fehlgeschlagen. Bitte erneut versuchen.";
        wrap.appendChild(errLine);
      }
      const gbtn = document.createElement("a");
      gbtn.className = "login-google";
      gbtn.href = "/api/auth/google/start";
      gbtn.setAttribute("role", "button");
      // 2026-06-30 (Martin): In der nativen App MUSS der Google-Login im
      // EXTERNEN Safari laufen — Google sperrt OAuth in App-WebViews
      // („Access Blocked"). Wir öffnen ?native=1 im System-Browser; nach der
      // Anmeldung kommt die App per Deep-Link (pawbot://login) zurück und
      // tauscht die Sitzung. Im Web bleibt der normale href-Klick.
      gbtn.addEventListener("click", (ev) => {
        const cap = (
          window as unknown as {
            Capacitor?: { isNativePlatform?: () => boolean };
          }
        ).Capacitor;
        if (cap?.isNativePlatform?.()) {
          ev.preventDefault();
          // Ohne Schutz meldete der Tausch in der App nicht an
          // (server/nativ-login-tausch.ts) — dann lieber gleich sagen.
          if (!schutzJetzt) {
            status.textContent = "Einen Moment – die Anmeldung wird vorbereitet. Bitte gleich noch einmal tippen.";
            status.dataset.kind = "error";
            schutz = holeAnmeldeSchutz();
            void schutz.then((w) => {
              schutzJetzt = w;
            });
            return;
          }
          window.open(
            "https://voice.vespera.at/api/auth/google/start?native=1&b=" +
              encodeURIComponent(schutzJetzt),
            "_system",
          );
        }
      });
      gbtn.innerHTML =
        '<svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>';
      gbtn.appendChild(document.createTextNode("Mit Google anmelden"));
      wrap.appendChild(gbtn);
      const divider = document.createElement("div");
      divider.className = "login-divider";
      divider.innerHTML = "<span>oder</span>";
      wrap.appendChild(divider);
    }

    const form = document.createElement("form");
    form.className = "login-form";
    form.noValidate = true;

    const emailLabel = document.createElement("label");
    emailLabel.className = "login-field";
    emailLabel.textContent = "E-Mail";
    const email = document.createElement("input");
    email.type = "email";
    email.required = true;
    email.autocomplete = "email";
    email.placeholder = "du@example.com";
    emailLabel.appendChild(email);
    form.appendChild(emailLabel);

    const pwLabel = document.createElement("label");
    pwLabel.className = "login-field";
    pwLabel.textContent = "Passwort";
    // Pre.270: Passwort-Feld mit Eye-Toggle (sichtbar machen).
    const pwWrap = document.createElement("div");
    pwWrap.className = "login-pw-wrap";
    const password = document.createElement("input");
    password.type = "password";
    password.required = true;
    password.autocomplete = "current-password";
    password.placeholder = "••••••••";
    pwWrap.appendChild(password);

    const eyeBtn = document.createElement("button");
    eyeBtn.type = "button";
    eyeBtn.className = "login-pw-eye";
    eyeBtn.setAttribute("aria-label", "Passwort anzeigen");
    eyeBtn.title = "Passwort anzeigen";
    eyeBtn.tabIndex = -1; // nicht über Tab — User soll im Input bleiben
    // Inline-SVG (eye / eye-off) — Lucide-Style, konsistent mit icons.ts.
    function renderEye(): void {
      eyeBtn.replaceChildren();
      const NS = "http://www.w3.org/2000/svg";
      const svg = document.createElementNS(NS, "svg");
      svg.setAttribute("width", "18");
      svg.setAttribute("height", "18");
      svg.setAttribute("viewBox", "0 0 24 24");
      svg.setAttribute("fill", "none");
      svg.setAttribute("stroke", "currentColor");
      svg.setAttribute("stroke-width", "2");
      svg.setAttribute("stroke-linecap", "round");
      svg.setAttribute("stroke-linejoin", "round");
      svg.setAttribute("aria-hidden", "true");
      // Lucide eye / eye-off
      const isVisible = password.type === "text";
      if (isVisible) {
        // eye-off
        const paths = [
          "M9.88 9.88a3 3 0 1 0 4.24 4.24",
          "M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68",
          "M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61",
          "M2 2l20 20",
        ];
        for (const d of paths) {
          const p = document.createElementNS(NS, "path");
          p.setAttribute("d", d);
          svg.appendChild(p);
        }
      } else {
        // eye
        const path1 = document.createElementNS(NS, "path");
        path1.setAttribute("d", "M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z");
        svg.appendChild(path1);
        const circle = document.createElementNS(NS, "circle");
        circle.setAttribute("cx", "12");
        circle.setAttribute("cy", "12");
        circle.setAttribute("r", "3");
        svg.appendChild(circle);
      }
      eyeBtn.appendChild(svg);
    }
    renderEye();
    eyeBtn.addEventListener("click", () => {
      const wasFocused = document.activeElement === password;
      password.type = password.type === "password" ? "text" : "password";
      eyeBtn.setAttribute(
        "aria-label",
        password.type === "text" ? "Passwort verstecken" : "Passwort anzeigen",
      );
      eyeBtn.title =
        password.type === "text" ? "Passwort verstecken" : "Passwort anzeigen";
      renderEye();
      // Cursor zurück ans Ende des Inputs
      if (wasFocused) {
        password.focus();
        const len = password.value.length;
        password.setSelectionRange(len, len);
      }
    });
    pwWrap.appendChild(eyeBtn);
    pwLabel.appendChild(pwWrap);
    form.appendChild(pwLabel);

    const submit = document.createElement("button");
    submit.type = "submit";
    submit.className = "login-submit";
    submit.textContent = "Einloggen";
    form.appendChild(submit);

    const status = document.createElement("div");
    status.className = "login-status";
    form.appendChild(status);

    const altWrap = document.createElement("div");
    altWrap.className = "login-alt";
    const altToggle = document.createElement("button");
    altToggle.type = "button";
    altToggle.className = "login-alt-toggle";
    altToggle.textContent = "Stattdessen Magic-Link an Mail schicken";
    altWrap.appendChild(altToggle);

    const magicForm = document.createElement("div");
    magicForm.className = "login-magic hidden";
    const magicBtn = document.createElement("button");
    magicBtn.type = "button";
    magicBtn.className = "login-magic-submit";
    magicBtn.textContent = "Magic-Link schicken";
    const magicStatus = document.createElement("div");
    magicStatus.className = "login-magic-status";
    magicForm.appendChild(magicBtn);
    magicForm.appendChild(magicStatus);
    altWrap.appendChild(magicForm);

    altToggle.addEventListener("click", () => {
      magicForm.classList.toggle("hidden");
    });

    magicBtn.addEventListener("click", async () => {
      const e = email.value.trim();
      if (!e.includes("@")) {
        magicStatus.textContent =
          "Bitte trag oben deine E-Mail ein, dann nochmal klicken.";
        magicStatus.dataset.kind = "error";
        return;
      }
      magicBtn.disabled = true;
      magicStatus.textContent = "wird gesendet…";
      delete magicStatus.dataset.kind;
      try {
        const result = await requestMagicLink(e);
        if (result.ok) {
          magicStatus.textContent = `Wenn die Mail erlaubt ist, ist der Link unterwegs an ${e}.`;
          magicStatus.dataset.kind = "ok";
        } else {
          magicStatus.textContent = `Fehler: ${result.error}${result.detail ? ` (${result.detail})` : ""}`;
          magicStatus.dataset.kind = "error";
        }
      } catch (err) {
        magicStatus.textContent = `Fehler: ${err instanceof Error ? err.message : String(err)}`;
        magicStatus.dataset.kind = "error";
      } finally {
        magicBtn.disabled = false;
      }
    });

    form.addEventListener("submit", async (ev) => {
      ev.preventDefault();
      const e = email.value.trim();
      const pw = password.value;
      if (!e.includes("@")) {
        status.textContent = "Das sieht nicht nach einer E-Mail aus.";
        status.dataset.kind = "error";
        return;
      }
      if (!pw) {
        status.textContent = "Passwort fehlt.";
        status.dataset.kind = "error";
        return;
      }
      submit.disabled = true;
      const oldLabel = submit.textContent;
      submit.textContent = "wird geprüft…";
      status.textContent = "";
      delete status.dataset.kind;
      try {
        const result = await loginWithPassword(e, pw, await schutz);
        if (result.error === "anmelde-schutz") {
          // Der Keks fehlt oder ist neu (anderer Tab, gelöschte Daten): neu holen.
          schutz = holeAnmeldeSchutz();
          void schutz.then((w) => {
            schutzJetzt = w;
          });
          status.textContent = "Die Anmeldung musste neu vorbereitet werden. Bitte noch einmal auf „Einloggen“ tippen.";
          status.dataset.kind = "error";
          submit.textContent = oldLabel ?? "Einloggen";
          submit.disabled = false;
          return;
        }
        if (result.ok) {
          status.textContent = "Eingeloggt — lade…";
          status.dataset.kind = "ok";
          onSuccess();
        } else {
          status.textContent =
            result.error === "invalid-credentials"
              ? "E-Mail oder Passwort stimmt nicht."
              : `Fehler: ${result.error}`;
          status.dataset.kind = "error";
          submit.textContent = oldLabel ?? "Einloggen";
          submit.disabled = false;
        }
      } catch (err) {
        status.textContent = `Fehler: ${err instanceof Error ? err.message : String(err)}`;
        status.dataset.kind = "error";
        submit.textContent = oldLabel ?? "Einloggen";
        submit.disabled = false;
      }
    });

    wrap.appendChild(form);
    wrap.appendChild(altWrap);
    root.appendChild(wrap);
  }
}
